import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';

/**
 * Document-themed progress dialog while the itinerary PDF is generated.
 */
const PdfExportProgressDialog = ({
  open,
  progress = 0,
  phase = 'preparing',
}) => {
  const { t } = useTranslation();
  const determinate = Number.isFinite(progress) && progress > 0;
  const labelKey = phase === 'downloading'
    ? 'export.pdfDownloading'
    : phase === 'done'
      ? 'export.pdfReady'
      : 'export.pdfPreparing';

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-labelledby="pdf-export-progress-title"
      PaperProps={{
        sx: {
          borderRadius: 0,
          border: `1px solid ${DOCUMENT.ruleHard}`,
          bgcolor: DOCUMENT.surface,
          minWidth: { xs: 'min(100%, 320px)', sm: 380 },
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: 'rgba(26, 14, 18, 0.35)' },
        },
      }}
    >
      <DialogTitle
        id="pdf-export-progress-title"
        sx={{
          fontFamily: DOCUMENT.display,
          fontWeight: 400,
          fontSize: 22,
          color: DOCUMENT.ink,
          pb: 1,
        }}
      >
        {t('export.pdfTitle')}
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 2.5 }}>
        <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mb: 1.5 }}>
          {t(labelKey)}
        </Typography>
        <Box data-testid="pdf-export-progress">
          <LinearProgress
            variant={determinate ? 'determinate' : 'indeterminate'}
            value={determinate ? Math.min(100, Math.max(0, progress)) : undefined}
            sx={{
              height: 6,
              borderRadius: 0,
              bgcolor: DOCUMENT.rule,
              '& .MuiLinearProgress-bar': {
                borderRadius: 0,
                bgcolor: DOCUMENT.oxblood,
              },
            }}
          />
          {determinate ? (
            <Typography
              sx={{
                mt: 1,
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: DOCUMENT.ink3,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {t('export.pdfPercent', { percent: Math.round(progress) })}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PdfExportProgressDialog;
